import React from 'react';
import { createRoot } from 'react-dom/client';
import { ApolloProvider } from '@apollo/client';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IntercomProvider } from 'react-use-intercom';
import { Auth0Provider } from '@auth0/auth0-react';

import './index.css';
import './v2/lib/registerPdfFonts';
import reportWebVitals from './reportWebVitals';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { apolloClient } from './lib/apolloHelpers';
import Config from './lib/config';
import { PatientAuthProvider } from './contexts/PatientAuthContext';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import SentryHelpers from './lib/sentry';
import { logger } from './lib/logger';
import { suppressNonErrorRejections } from './lib/suppressNonErrorRejections';

// Install before Sentry.init() so non-informative promise rejections (e.g. the
// stack-less Auth0 `/auth-callback` rejections, Sentry issues WEB-9W/WEB-9V)
// are swallowed before Sentry's GlobalHandlers can capture them as `<unknown>`.
suppressNonErrorRejections();
SentryHelpers.initSentry();

// A Messenger that fails to load is otherwise silent: the launcher never
// appears, or renders Intercom's own error panel inside its iframe, and nothing
// reaches Sentry. Providers then look like they have support available when
// they don't. Report it so a broken Messenger is an alert rather than a ticket.
const handleIntercomLoadFailed = () => {
  logger.error('Intercom Messenger failed to load', {
    // Public value (it ships in the bundle); included because an empty app ID
    // from a missing REACT_APP_INTERCOM_APP_ID looks identical to an outage.
    appId: Config.REACT_APP_INTERCOM_APP_ID,
  });
};

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <ApolloProvider client={apolloClient}>
      <Auth0Provider
        domain={Config.REACT_APP_AUTH0_DOMAIN}
        clientId={Config.REACT_APP_AUTH0_CLIENT_ID}
        authorizationParams={{
          audience: Config.REACT_APP_AUTH0_API_AUDIENCE,
          redirect_uri: Config.REACT_APP_AUTH0_REDIRECT_URI,
        }}
      >
        <IntercomProvider
          appId={Config.REACT_APP_INTERCOM_APP_ID}
          autoBoot
          onLoadFailed={handleIntercomLoadFailed}
        >
          <BrowserRouter>
            <AuthProvider>
              <PatientAuthProvider>
                {/* Drag and drop provider. */}
                <DndProvider backend={HTML5Backend}>
                  <HelmetProvider>
                    <App />
                  </HelmetProvider>
                </DndProvider>
              </PatientAuthProvider>
            </AuthProvider>
          </BrowserRouter>
        </IntercomProvider>
      </Auth0Provider>
    </ApolloProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
